const STREAK_KEY = 'diwanStreak';

const ZERO_STREAK = {
  streak_count:   0,
  best_streak:    0,
  streak_shield:  false,
  last_milestone: null,
};

// Returns streak state from localStorage; falls back to zero state for new users.
export function readStreak() {
  try {
    const raw = localStorage.getItem(STREAK_KEY);
    if (!raw) return { ...ZERO_STREAK };
    return { ...ZERO_STREAK, ...JSON.parse(raw) };
  } catch {
    return { ...ZERO_STREAK };
  }
}

// Writes the day-1 streak record the first time onboarding completes.
// No-op if a record already exists — streak progress is never reset.
export function initStreak() {
  try {
    if (localStorage.getItem(STREAK_KEY)) return;
    localStorage.setItem(STREAK_KEY, JSON.stringify({
      ...ZERO_STREAK,
      streak_count:     1,
      best_streak:      1,
      last_active_date: new Date().toISOString().slice(0, 10),
    }));
  } catch { /* storage blocked */ }
}

// Call once on home screen mount. Advances the counter for a new calendar day,
// resets to 1 on a gap, and is a no-op if already called today.
// 1-day grace period: missing exactly one day does not break the streak.
export function tickStreak() {
  const today = new Date().toISOString().slice(0, 10);
  const state = readStreak();
  const last  = state.last_active_date;

  if (last === today) return state; // already counted today

  const yesterday  = new Date(Date.now() -     86_400_000).toISOString().slice(0, 10);
  const twoDaysAgo = new Date(Date.now() - 2 * 86_400_000).toISOString().slice(0, 10);

  const continues = last === yesterday || last === twoDaysAgo;
  const newCount  = continues ? state.streak_count + 1 : 1;

  const newState = {
    ...state,
    streak_count:     newCount,
    best_streak:      Math.max(state.best_streak, newCount),
    last_active_date: today,
  };

  try { localStorage.setItem(STREAK_KEY, JSON.stringify(newState)); } catch { /* storage blocked */ }
  return newState;
}

export function getMilestone(streakCount) {
  if (streakCount >= 365) return {
    key: 'm365', arabic: 'الحكيم', title: 'The Sage',
    sub: 'A full year. Poetry lives in you now.',
    icon: '🏛', nextTarget: null,
  };
  if (streakCount >= 100) return {
    key: 'm100', arabic: 'العارف', title: 'The Knower',
    sub: 'One who has truly entered',
    icon: '⭐', nextTarget: 365,
  };
  if (streakCount >= 30) return {
    key: 'm30', arabic: 'المواظب', title: 'Devoted Reader',
    sub: 'One who returns faithfully',
    icon: '🌙', nextTarget: 100,
  };
  if (streakCount >= 7) return {
    key: 'm7', arabic: 'المثابر', title: 'First Week',
    sub: 'The Persistent One',
    icon: '✦', nextTarget: 30,
  };
  const next = streakCount >= 100 ? 365
             : streakCount >= 30  ? 100
             : streakCount >= 7   ? 30 : 7;
  return { key: 'between', nextTarget: next };
}

export function getComebackCopy(bestStreak, lastMilestoneKey) {
  if (!lastMilestoneKey) {
    return `Poetry rewards those who return. Your best was ${bestStreak} days — start today and beat it.`;
  }
  const lines = {
    m7:   `You reached المثابر once. The path back is only 7 days.`,
    m30:  `You reached المواظب once. The path back is shorter than the one you already walked.`,
    m100: `العارف does not forget what they have learned. Come back.`,
    m365: `الحكيم has walked this road before. One missed day does not erase a year.`,
  };
  return lines[lastMilestoneKey];
}

export function getPipConfig(streakCount) {
  if (streakCount >= 365) return { cols: 13, total: 52, filled: 52 };
  if (streakCount >= 100) return { cols: 5,  total: 15, filled: Math.floor(streakCount / 7) };
  if (streakCount >= 30)  return { cols: 4,  total: 4,  filled: Math.floor(streakCount / 7) };
  return null;
}
